import { ApolloClient, InMemoryCache, useQuery } from '@apollo/client';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';
import { MilleFeuille, MilleFeuilleMain } from '@/layouts/MilleFeuille';
import {
  getWebflowHybridPageProps,
  WebflowHybridPageProps,
} from '@/lib/webflow-poc';
import { WebflowPageShell } from '@/lib/webflow-poc/WebflowPageShell';
import { FooterRender, PageRender } from '@/modules/website-builder';
import { CURRENT_USER_ROOT_URL, HOME_PAGE_AND_TENANT } from '@/queries';
import { CurrentUserRootUrl } from '@/queries/graphql-types/CurrentUserRootUrl';
import { HomePageAndTenant } from '@/queries/graphql-types/HomePageAndTenant';

type IndexPageProps = {
  content: React.ComponentProps<typeof PageRender>['content'] | null;
  tenant: React.ComponentProps<typeof FooterRender>['tenant'] | null;
  webflow: WebflowHybridPageProps['webflow'] | null;
  navigationChrome: WebflowHybridPageProps['navigationChrome'] | null;
  webflowTenant: WebflowHybridPageProps['tenant'];
};

type TenantWithWebflowFlag = HomePageAndTenant['tenant']['result'] & {
  featureFlags?: { webflow?: boolean | null } | null;
};

export const getServerSideProps: GetServerSideProps<IndexPageProps> = async (
  context
) => {
  try {
    const client = new ApolloClient({
      ssrMode: true,
      uri: process.env.NEXT_PUBLIC_APOLLO_HTTP_URI,
      cache: new InMemoryCache(),
    });

    const query = await client.query<HomePageAndTenant>({
      query: HOME_PAGE_AND_TENANT,
      context: {
        headers: {
          tenant_domain: context.req?.headers.tenant_domain,
          authorization: `Bearer ${context.req.cookies['accounts%3AaccessToken']}`,
        },
      },
      fetchPolicy: 'no-cache',
    });

    const tenant = (query.data.tenant?.result ??
      null) as TenantWithWebflowFlag | null;

    // Webflow is consulted ONLY when the tenant's webflow feature flag is
    // on — the flag is read from this CMS tenant query, so flag-off
    // tenants run the pre-Webflow Website Builder flow untouched, with
    // zero Webflow GraphQL/upstream calls.
    if (tenant?.featureFlags?.webflow === true) {
      const hybrid = await getWebflowHybridPageProps(context, {
        skipFeatureFlagCheck: true,
      });

      if (hybrid.kind === 'ok') {
        return {
          props: JSON.parse(
            JSON.stringify({
              content: null,
              tenant: null,
              webflow: hybrid.props.webflow,
              navigationChrome: hybrid.props.navigationChrome,
              webflowTenant: hybrid.props.tenant,
            })
          ) as IndexPageProps,
        };
      }

      // Any other hybrid outcome (not integrated, no Webflow page,
      // upstream error) falls through to the CMS homepage below —
      // Webflow must never break Website Builder rendering.
      if (hybrid.kind === 'error') {
        console.error('[webflow-hybrid] home error, falling back to CMS', {
          resolvedUrl: context.resolvedUrl,
          statusCode: hybrid.statusCode,
        });
      }
    }

    if (!query.data.homePage?.content || !tenant) {
      return {
        props: {
          content: null,
          tenant: null,
          webflow: null,
          navigationChrome: null,
          webflowTenant: null,
        },
      };
    }

    return {
      props: {
        content: query.data.homePage.content,
        tenant,
        webflow: null,
        navigationChrome: null,
        webflowTenant: null,
      },
    };
  } catch (error) {
    if (error?.networkError?.statusCode === 422) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        content: null,
        tenant: null,
        webflow: null,
        navigationChrome: null,
        webflowTenant: null,
      },
    };
  }
};

const Redirect: React.FC = () => {
  const router = useRouter();

  const { data, loading } = useQuery<CurrentUserRootUrl>(CURRENT_USER_ROOT_URL);

  const rootUrl = data?.tenant?.result?.currentUserLandingPageUrl;

  React.useEffect(() => {
    if (router.isReady && !loading) {
      if (rootUrl) {
        router.replace({ pathname: rootUrl, query: router.query });
      } else {
        router.replace({
          pathname: '/login',
          query: {
            redirect: router.asPath,
          },
        });
      }
    }
  }, [loading, router, rootUrl]);

  return <Loading />;
};

const IndexPage: React.FC<IndexPageProps> = (props) => {
  if (props.webflow && props.navigationChrome) {
    return (
      <WebflowPageShell
        webflow={props.webflow}
        navigationChrome={props.navigationChrome}
        tenant={props.webflowTenant}
      />
    );
  }

  if (props.content && props.tenant) {
    return (
      <MilleFeuille>
        <MilleFeuilleMain>
          <PageRender content={props.content} />
        </MilleFeuilleMain>
        <FooterRender tenant={props.tenant} />
      </MilleFeuille>
    );
  }

  return <Redirect />;
};

export default IndexPage;
